import Dynamic from '../dynamic/Dynamic'
import { Device } from '../helpers'
import { domReady } from '../helpers/DOM'
import Logger from '../helpers/Logger'
import { Hint } from '../hint/Hint'
import WebModel from '../models/WebModel'
import PrebidFacade from '../prebidFacade/PrebidFacade'
import { Slot } from '../slots/Slot'
import SlotFactory from '../slots/SlotFactory'
import { getQueryParam } from '../helpers/getQueryParam'

export default class DynamicContentSlots {
  static readonly MOBILE_SELECTOR = '.content_mobile_hint, .content_hint'
  static readonly DESKTOP_SELECTOR = '.content_desktop_hint, .content_hint'
  static readonly DATA_CONTENT_SELECTOR = '[data-content-area]'
  static readonly SLOT_CLASS: SlotClass = 'Content'

  slots: Array<Slot | undefined> = []
  private readonly startingParagraphNumber: number
  private readonly endingParagraphNumber: number
  private readonly contentRequireText: number
  private readonly stopSelector: string | undefined
  private readonly minParagraphBuffer: number | undefined
  private readonly slotLimit: number | undefined
  private readonly density: number
  private readonly target: string | undefined
  private readonly divBuster: boolean
  private readonly unbustableSelector: string

  constructor(
    model: WebModel,
    private readonly prebidFacade: PrebidFacade,
    private readonly slotFactory: SlotFactory
  ) {
    // Don't create Dynamic Content slots if the publisher has them turned off.
    if (Device.isMobileOrTablet && !model.content_mobile) {
      return
    } else if (!Device.isMobileOrTablet && !model.content_desktop) {
      return
    }

    this.startingParagraphNumber = model.content_buffer_begin
    this.endingParagraphNumber = model.content_buffer_end
    this.contentRequireText = model.content_require_text
    this.stopSelector = model.content_stop_selector

    this.minParagraphBuffer = model.content_cba_desktop_buffer
    this.density =
      model.content_cba_desktop_percentage &&
      model.content_cba_desktop_percentage / 100.0
    this.slotLimit = model.content_cba_desktop_limit
    this.target = model.content_selector

    this.divBuster = model.enable_divbuster
    this.unbustableSelector = model.divbuster_exclusions

    // Deal with `enableDivbuster` query parameter
    const divBusterQP = getQueryParam('enableDivbuster') as string
    if (divBusterQP !== undefined) {
      if (divBusterQP !== '') {
        this.target = divBusterQP
      }
      this.divBuster = true
    }

    if (Device.isMobileOrTablet) {
      this.minParagraphBuffer = model.content_cba_mobile_buffer
      this.density =
        model.content_cba_mobile_percentage &&
        model.content_cba_mobile_percentage / 100.0
      this.slotLimit = model.content_cba_mobile_limit
      this.target = model.content_selector_mobile || model.content_selector
    }

    // Expose this.dynamicallyCreateSlots to the outside world so pubs can dynamically
    // insert ads into dynamic content, like Infinite Scrollers or Single Page Apps.
    window.$mediavine.web.insertContentAds = this.dynamicallyCreateSlots.bind(
      this
    )

    domReady(() => {
      this.render()
    })
  }

  render(): void {
    const hasContentHints =
      Hint.getHintParagraphs({
        mobileSelector: DynamicContentSlots.MOBILE_SELECTOR,
        desktopSelector: DynamicContentSlots.DESKTOP_SELECTOR
      }).length > 0
    const contentElements = this.target
      ? Array.from(
          document.querySelectorAll(
            `${this.target}, ${DynamicContentSlots.DATA_CONTENT_SELECTOR}`
          )
        )
      : []

    // On first load, insert content ads only if hints are not present.
    if (!hasContentHints) {
      contentElements.forEach((el) => this.dynamicallyCreateSlots(el))
    }
  }

  dynamicallyCreateSlots(target: HTMLElement | Element | null): boolean {
    if (!target || !(target instanceof HTMLElement)) {
      Logger.debug(
        `Mediavine: Must provide a valid element to insert ads into.`,
        target
      )

      return false
    }

    if (target.getAttribute('data-content-ads-inserted') === 'true') {
      // Don't want to double-insert ads into an element
      // Logger.debug(
      //   `Mediavine: Can't insert ads into the same element twice.`,
      //   target
      // )

      return false
    }
    target.setAttribute('data-content-ads-inserted', 'true')

    const dynamic = new Dynamic({
      slotFactory: this.slotFactory,
      slotClass: 'Content',
      prebidFacade: this.prebidFacade,
      target,
      slotLimit: this.slotLimit,
      startingParagraphNumber: this.startingParagraphNumber,
      endingParagraphNumber: this.endingParagraphNumber,
      density: this.density,
      contentRequireText: this.contentRequireText,
      minParagraphBuffer: this.minParagraphBuffer,
      stopSelector: this.stopSelector,
      divBuster: this.divBuster,
      unbustableSelector: this.unbustableSelector
    })

    dynamic.createSlots()

    return true
  }
}
